import type { Choice } from '@pons/contracts';
import type { CoinView } from './model';
import { betMultiplier } from './predictionState';

export function previewBet(coin: CoinView) {
  if (!coin.choice) return null;
  const final =
    !coin.cancelled && coin.phase === 'settled' && coin.outcome !== null;
  return {
    choice: coin.choice,
    result: final ? (coin.choice === coin.outcome ? 'win' : 'lose') : null,
    multiplier: betMultiplier(coin),
  };
}

function capChange(coin: CoinView) {
  const cap = coin.marketCap;
  const graduation = coin.graduationCap;
  if (
    cap === null ||
    !Number.isFinite(cap) ||
    cap < 0 ||
    graduation === null ||
    !Number.isFinite(graduation) ||
    graduation <= 0
  )
    return null;
  return cap / graduation - 1;
}

export function previewStatus(coin: CoinView, now: number) {
  const final = coin.phase === 'settled' && coin.outcome !== null;
  const pending = !coin.cancelled && !final && now >= coin.settlesAt;
  const change = capChange(coin);
  const choice: Choice | null = final
    ? coin.outcome
    : coin.cancelled || pending || change === null
      ? null
      : change >= 0
        ? 'RUN'
        : 'DIE';
  const kind = coin.cancelled
    ? 'cancelled'
    : final
      ? 'final'
      : pending
        ? 'pending'
        : choice
          ? 'active'
          : 'unavailable';
  const total = Math.max(1, coin.settlesAt - coin.closesAt);
  const elapsed = Math.min(total, Math.max(0, now - coin.closesAt));
  return {
    choice,
    kind,
    percent: final ? coin.resultPercent : change === null ? null : change * 100,
    total,
    elapsed,
    remaining: Math.max(0, coin.settlesAt - now),
  };
}

// The compact scale uses the same graduation-relative range as the main chart.
// A newer quote can extend the range, but never changes which side of zero it is on.
export function previewPosition(coin: CoinView) {
  const change = capChange(coin);
  if (change === null) return null;
  let span = Math.max(0.02, Math.abs(change));
  if (
    coin.baseline !== null &&
    coin.baseline > 0 &&
    Number.isFinite(coin.baseline)
  ) {
    for (const price of coin.history) {
      if (Number.isFinite(price) && price > 0)
        span = Math.max(span, Math.abs(price / coin.baseline - 1));
    }
  }
  return change / span;
}

// 229px track, 1px stroke, 14px padding and a 2px dot radius.
export const PREVIEW_SCALE_EDGE = 229 / 2 - 1 - 14 - 2;
export function previewDotOffsets(
  pillWidth: number,
  far = PREVIEW_SCALE_EDGE,
  count = 10,
) {
  const near = Math.min(far, pillWidth / 2 + 6);
  const step = (far - near) / Math.max(1, count - 1);
  return [
    Array.from({ length: count }, (_, i) => -far + step * i),
    Array.from({ length: count }, (_, i) => near + step * i),
  ];
}

// Enlarge the existing dots under the moving arrow without shifting their centres.
export function previewDotScale(dot: number, cursor: number | null) {
  if (cursor === null) return 1;
  const proximity = Math.max(0, 1 - Math.abs(dot - cursor) / 7);
  return 1 + proximity * proximity * (3 - 2 * proximity);
}
